import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Users, Calendar, Newspaper, Film, Plus, Mail } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import CrudFormField from '@/components/admin/CrudFormField';
import CrudTable from '@/components/admin/CrudTable';
import DashboardHeader from '@/components/admin/DashboardHeader';
import StatsGrid from '@/components/admin/StatsGrid';
import SubscribersTable from '@/components/admin/SubscribersTable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: 'artists', label: 'Artists', icon: Users },
  { key: 'events', label: 'Events', icon: Calendar },
  { key: 'news', label: 'News', icon: Newspaper },
  { key: 'media', label: 'Media', icon: Film },
  { key: 'subscribers', label: 'Subscribers', icon: Mail },
];

function parseErrorMessage(err) {
  const detail = err.response?.data?.detail;
  if (!detail) return 'Failed to save';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join(', ');
  return String(detail);
}

// ── CRUD Dialog (create/edit) ────────────────────

function CrudDialog({ open, onOpenChange, editing, fields, form, setForm, onSave, saving }) {
  const resource = editing?._resource || 'item';
  const handleFieldChange = useCallback((key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  }, [setForm]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#122036] border-[#1c2a3d] text-[#fdfbf7] max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Cormorant_Garamond'] text-2xl">
            {editing ? 'Edit' : 'Add'} {resource}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {fields.map(f => (
            <CrudFormField key={f.key} field={f} value={form[f.key]} onChange={handleFieldChange} />
          ))}
        </div>
        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="text-[#a3b1c6] hover:text-[#fdfbf7] px-4 py-2 text-sm transition-colors">Cancel</button>
          <button
            data-testid="form-save-btn"
            onClick={onSave}
            disabled={saving}
            className="bg-[#d4af37] text-[#0a1628] hover:bg-[#b49129] font-medium px-6 py-2 rounded-md transition-all text-sm disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── CRUD Panel (composes Table + Dialog) ─────────

function CrudPanel({ resource, fields, data, onRefresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const displayFields = useMemo(() => fields.filter(f => f.showInTable !== false), [fields]);
  const singularName = resource.slice(0, -1);
  const defaultForm = useCallback(() => fields.reduce((acc, f) => ({ ...acc, [f.key]: f.default || '' }), {}), [fields]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setForm(defaultForm());
    setDialogOpen(true);
  }, [defaultForm]);

  const openEdit = useCallback((item) => {
    setEditing({ ...item, _resource: singularName });
    setForm(fields.reduce((acc, f) => ({ ...acc, [f.key]: item[f.key] ?? f.default ?? '' }), {}));
    setDialogOpen(true);
  }, [fields, singularName]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/${resource}/${editing.id}`, form);
        toast.success(`${singularName} updated`);
      } else {
        await axios.post(`${API}/${resource}`, form);
        toast.success(`${singularName} created`);
      }
      setDialogOpen(false);
      onRefresh();
    } catch (err) {
      toast.error(parseErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }, [editing, form, resource, singularName, onRefresh]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await axios.delete(`${API}/${resource}/${id}`);
      toast.success('Deleted successfully');
      onRefresh();
    } catch {
      toast.error('Failed to delete');
    }
  }, [resource, onRefresh]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-['Cormorant_Garamond'] text-2xl text-[#fdfbf7] capitalize">{resource}</h3>
        <button
          data-testid={`admin-add-${singularName}`}
          onClick={openCreate}
          className="bg-[#d4af37] text-[#0a1628] hover:bg-[#b49129] font-medium px-4 py-2 rounded-md transition-all duration-300 flex items-center gap-2 text-sm"
        >
          <Plus className="w-4 h-4" /> Add {singularName}
        </button>
      </div>

      <CrudTable data={data} displayFields={displayFields} onEdit={openEdit} onDelete={handleDelete} />

      <CrudDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        fields={fields}
        form={form}
        setForm={setForm}
        onSave={handleSave}
        saving={saving}
      />
    </div>
  );
}

// ── Field Definitions ──────────────────────────

const ARTIST_FIELDS = [
  { key: 'name', label: 'Name', showInTable: true },
  { key: 'title', label: 'Title', showInTable: true },
  { key: 'bio', label: 'Bio', type: 'textarea', showInTable: false },
  { key: 'image_url', label: 'Image URL', showInTable: false },
  { key: 'era', label: 'Era', showInTable: true },
  { key: 'genre', label: 'Genre', showInTable: true },
  { key: 'notable_works', label: 'Notable Works', type: 'textarea', showInTable: false },
  { key: 'featured', label: 'Featured', type: 'boolean', default: false, showInTable: true },
];

const EVENT_FIELDS = [
  { key: 'title', label: 'Title', showInTable: true },
  { key: 'description', label: 'Description', type: 'textarea', showInTable: false },
  { key: 'date', label: 'Date', type: 'date', showInTable: true },
  { key: 'location', label: 'Location', showInTable: true },
  { key: 'image_url', label: 'Image URL', showInTable: false },
  { key: 'event_type', label: 'Type', type: 'select', options: ['concert', 'workshop', 'festival'], showInTable: true },
];

const NEWS_FIELDS = [
  { key: 'title', label: 'Title', showInTable: true },
  { key: 'content', label: 'Content', type: 'textarea', showInTable: false },
  { key: 'image_url', label: 'Image URL', showInTable: false },
  { key: 'author', label: 'Author', showInTable: true },
  { key: 'category', label: 'Category', type: 'select', options: ['general', 'feature', 'release', 'interview'], showInTable: true },
];

const MEDIA_FIELDS = [
  { key: 'title', label: 'Title', showInTable: true },
  { key: 'description', label: 'Description', type: 'textarea', showInTable: false },
  { key: 'embed_url', label: 'Embed URL', showInTable: true },
  { key: 'media_type', label: 'Type', type: 'select', options: ['youtube', 'spotify', 'soundcloud'], showInTable: true },
  { key: 'thumbnail_url', label: 'Thumbnail URL', showInTable: false },
  { key: 'artist_name', label: 'Artist', showInTable: true },
];

// ── Main Dashboard ─────────────────────────────

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [artists, setArtists] = useState([]);
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [media, setMedia] = useState([]);
  const [subscribers, setSubscribers] = useState([]);

  const fetchAll = useCallback(async () => {
    try {
      const [a, e, n, m, s] = await Promise.all([
        axios.get(`${API}/artists`),
        axios.get(`${API}/events`),
        axios.get(`${API}/news`),
        axios.get(`${API}/media`),
        axios.get(`${API}/newsletter/subscribers`),
      ]);
      setArtists(a.data);
      setEvents(e.data);
      setNews(n.data);
      setMedia(m.data);
      setSubscribers(s.data);
    } catch {
      toast.error('Failed to load dashboard data');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- API is a module constant

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/admin/login');
  }, [logout, navigate]);

  const stats = useMemo(() => [
    { label: 'Artists', count: artists.length, icon: Users },
    { label: 'Events', count: events.length, icon: Calendar },
    { label: 'News', count: news.length, icon: Newspaper },
    { label: 'Media', count: media.length, icon: Film },
    { label: 'Subscribers', count: subscribers.length, icon: Mail },
  ], [artists.length, events.length, news.length, media.length, subscribers.length]);

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="admin-dashboard">
      <DashboardHeader email={user?.email} onLogout={handleLogout} />

      <div className="max-w-7xl mx-auto px-6 md:px-8 py-8">
        <StatsGrid stats={stats} />

        <Tabs defaultValue="artists" className="w-full">
          <TabsList className="bg-[#122036] border border-[#1c2a3d] p-1 rounded-lg mb-6 flex flex-wrap">
            {TABS.map(t => (
              <TabsTrigger
                key={t.key}
                value={t.key}
                data-testid={`tab-${t.key}`}
                className="flex items-center gap-2 data-[state=active]:bg-[#d4af37] data-[state=active]:text-[#0a1628] text-[#a3b1c6] px-4 py-2 rounded-md text-sm transition-all"
              >
                <t.icon className="w-4 h-4" /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="artists">
            <CrudPanel resource="artists" fields={ARTIST_FIELDS} data={artists} onRefresh={fetchAll} />
          </TabsContent>
          <TabsContent value="events">
            <CrudPanel resource="events" fields={EVENT_FIELDS} data={events} onRefresh={fetchAll} />
          </TabsContent>
          <TabsContent value="news">
            <CrudPanel resource="news" fields={NEWS_FIELDS} data={news} onRefresh={fetchAll} />
          </TabsContent>
          <TabsContent value="media">
            <CrudPanel resource="media" fields={MEDIA_FIELDS} data={media} onRefresh={fetchAll} />
          </TabsContent>
          <TabsContent value="subscribers">
            <SubscribersTable subscribers={subscribers} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
